import { IConfigStore } from "./configstore";
import { SYFT_VERSION, type Event, type UploadRequest } from "./types";

/**
 * Options used when initializing the uploader.
 */
export interface InitOptions {
  url: string;
  sourceId?: string;
  replicateTo?: string[];
}

const BATCH_SIZE = 10;
const MAX_WAITING_TIME = 9000;
const RETRIES = 3;
// Browsers cap the total body size of in-flight `keepalive` requests at 64KiB
// per page; stay under it so requests can outlive the page.
const KEEPALIVE_MAX_BYTES = 60 * 1024;
const IDENTIFY_EVENT_KEY = "identify_event";

function byteSize(value: string): number {
  return new Blob([value]).size;
}

export class BatchUploader {
  store: IConfigStore;

  identifyEvent?: Event;
  events: Event[] = [];

  sourceId?: string;
  url: string;
  replicateTo: string[];

  // state
  isUploading = false;
  oldestEventTimestamp = 0; // oldest event timestamp in the queue.
  currentRetryCount = 0;
  currentTimeout?: NodeJS.Timeout; // current timeout for the upload.
  abortControllers = new Set<AbortController>();
  isDisposed = false;

  constructor(options: InitOptions, store: IConfigStore) {
    const { sourceId, url, replicateTo = [] } = options;
    this.sourceId = sourceId;
    this.url = url;
    this.replicateTo = replicateTo;
    this.store = store;
    this.identifyEvent = this.store.get(IDENTIFY_EVENT_KEY) as Event;
    if (this.identifyEvent != null) {
      this.addToQueue(this.identifyEvent);
    }
  }

  addToQueue(event: Event): boolean {
    if (this.isDisposed) return false;

    if (this.events.length === 0) {
      this.oldestEventTimestamp = Date.now();
    }

    this.events.push(event);
    const isIdentify = event.type === "identify" || event.type === "signup";
    if (isIdentify) {
      this.identifyEvent = event;
      this.store.set(IDENTIFY_EVENT_KEY, event);
    }
    this.flushIfRequired(isIdentify);
    return true;
  }

  flushIfRequired(force: boolean = false): void {
    if (this.isUploading) return;
    const now = Date.now();
    const oldestEventWaitingTime = now - this.oldestEventTimestamp;
    if (
      this.events.length >= BATCH_SIZE ||
      oldestEventWaitingTime >= MAX_WAITING_TIME ||
      force
    ) {
      this.__flush();
    } else {
      if (this.currentTimeout === undefined) {
        const remainingTime = MAX_WAITING_TIME - oldestEventWaitingTime;
        this.currentTimeout = setTimeout(() => {
          this.flushIfRequired();
        }, remainingTime);
      }
    }
  }

  /**
   * Sends every queued event right away because the page is being hidden or
   * unloaded. Unlike flushIfRequired(), it does not wait for an upload that is
   * already in flight and does not retry: the page may not live long enough.
   * Requests use `keepalive` so the browser completes them after unload.
   */
  flushOnPageHide(): void {
    if (this.isDisposed || this.events.length === 0) return;
    if (typeof window !== "undefined" && window.syftc?.enabled === false)
      return;

    clearTimeout(this.currentTimeout);
    this.currentTimeout = undefined;
    const events = this.events.splice(0, this.events.length);
    this.chunkForKeepalive(events).forEach((chunk) => {
      this.upload(chunk)
        .then(() => this.onUploaded(chunk))
        .catch(() => {});
    });
  }

  /**
   * Splits events into batches whose request body fits the keepalive limit.
   * A single event larger than the limit is sent on its own (without keepalive).
   */
  chunkForKeepalive(events: Event[]): Event[][] {
    const chunks: Event[][] = [];
    let current: Event[] = [];
    let currentSize = byteSize(JSON.stringify(this.buildRequest([])));
    const emptySize = currentSize;
    for (const event of events) {
      // +1 for the comma separating events in the array.
      const eventSize = byteSize(JSON.stringify(event)) + 1;
      if (current.length > 0 && currentSize + eventSize > KEEPALIVE_MAX_BYTES) {
        chunks.push(current);
        current = [];
        currentSize = emptySize;
      }
      current.push(event);
      currentSize += eventSize;
    }
    if (current.length > 0) chunks.push(current);
    return chunks;
  }

  private buildRequest(events: Event[]): UploadRequest {
    return {
      events,
      sourceId: this.sourceId,
      version: SYFT_VERSION,
      sentAt: new Date(),
    };
  }

  __flush = (): void => {
    if (this.isDisposed) return;

    if (this.events.length === 0) {
      // show the stack trace to find out where this is called from.
      return;
    }

    if (typeof window !== "undefined" && window.syftc?.enabled === false)
      return;
    const events = this.events.splice(0, this.events.length);
    this.isUploading = true;

    clearTimeout(this.currentTimeout);
    this.currentTimeout = undefined;

    this.upload(events)
      .then(() => {
        this.currentRetryCount = 0;
        this.isUploading = false;
        this.onUploaded(events);
      })
      .catch(() => {
        if (this.isDisposed) return;

        if (this.currentRetryCount >= RETRIES) {
          // drop events
          // console.warn('dropping events', events.length, e);
          this.currentRetryCount = 0;
          this.isUploading = false;
          return;
        }
        // put back events.
        this.events = [...events, ...this.events];
        this.oldestEventTimestamp = 0;
        this.currentRetryCount = this.currentRetryCount + 1;
        // attempting to retry
        this.currentTimeout = setTimeout(() => {
          this.__flush();
        }, this.currentRetryCount * 2000);
      });
  };

  private onUploaded(events: Event[]): void {
    if (this.identifyEvent != null && events.includes(this.identifyEvent)) {
      this.identifyEvent = undefined;
      this.store.remove(IDENTIFY_EVENT_KEY);
    }
  }

  async upload(events: Event[]): Promise<boolean> {
    const body = JSON.stringify(this.buildRequest(events));
    // keepalive lets the request finish if the page unloads mid-upload.
    const keepalive = byteSize(body) <= KEEPALIVE_MAX_BYTES;
    const abortController = new AbortController();
    this.abortControllers.add(abortController);
    return await new Promise<boolean>((resolve, reject) => {
      fetch(this.url, {
        priority: "low",
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        mode: "no-cors",
        keepalive,
        signal: abortController.signal,
        body,
      })
        .then(() => {
          // Handle replication if main request succeeds
          const replicationRequests = this.replicateTo.map((url) =>
            fetch(url, {
              priority: "low",
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
              },
              mode: "no-cors",
              signal: abortController.signal,
              body,
            }).catch(() => {})
          );
          Promise.allSettled(replicationRequests).finally(() => {
            this.abortControllers.delete(abortController);
          });
          return resolve(true);
        })
        .catch((error) => {
          this.abortControllers.delete(abortController);
          reject(new Error(`Failed to upload events: ${error.message}`));
        });
    });
  }

  discard(): void {
    this.isDisposed = true;
    this.abortControllers.forEach((controller) => controller.abort());
    this.abortControllers.clear();
    clearTimeout(this.currentTimeout);
    this.currentTimeout = undefined;
    this.events = [];
    this.identifyEvent = undefined;
    this.currentRetryCount = 0;
    this.isUploading = false;
    this.store.remove(IDENTIFY_EVENT_KEY);
  }
}
